import {
    Engine,
    FreeCamera,
    ICameraInput,
    KeyboardEventTypes,
    KeyboardInfo,
    Observer,
    Scene,
    Vector3,
} from "@babylonjs/core"

// This is mostly straight from the babylonjs camera input docs, so ignore the whole file.
/* tslint:disable */

/**
 * Manage the keyboard inputs to control the movement of a free camera.
 * @see http://doc.babylonjs.com/how_to/customizing_camera_inputs
 */
export class SimpleCameraInput implements ICameraInput<FreeCamera> {
    /**
     * Defines the camera the input is attached to.
     */
    camera: FreeCamera

    /**
     * Gets or Set the list of keyboard keys used to control the forward move of the camera.
     */
    keysUp = ["ArrowUp", "w", "W", "8"]

    /**
     * Gets or Set the list of keyboard keys used to control the backward move of the camera.
     */
    keysDown = ["ArrowDown", "s", "S", "2"]

    /**
     * Gets or Set the list of keyboard keys used to control the left strafe move of the camera.
     */
    keysLeft = ["ArrowLeft", "a", "A", "4"]

    /**
     * Gets or Set the list of keyboard keys used to control the right strafe move of the camera.
     */
    keysRight = ["ArrowRight", "d", "D", "6"]

    private _keys: Array<string> = []
    private _onCanvasBlurObserver: Observer<Engine>
    private _onKeyboardObserver: Observer<KeyboardInfo>
    private _engine: Engine
    private _scene: Scene

    /**
     * Attach the input controls to a specific dom element to get the input from.
     * @param noPreventDefault Defines whether event caught by the controls should call preventdefault()
     *     (https://developer.mozilla.org/en-US/docs/Web/API/Event/preventDefault)
     */
    attachControl(noPreventDefault?: boolean): void {
        if (this._onCanvasBlurObserver) {
            return
        }

        this._scene = this.camera.getScene()
        this._engine = this._scene.getEngine()

        this._onCanvasBlurObserver = this._engine.onCanvasBlurObservable.add(
            () => {
                this._keys = []
            },
        )

        this._onKeyboardObserver = this._scene.onKeyboardObservable.add(
            (info) => {
                const evt = info.event

                if (info.type === KeyboardEventTypes.KEYDOWN) {
                    if (
                        this.keysUp.indexOf(evt.key) !== -1 ||
                        this.keysDown.indexOf(evt.key) !== -1 ||
                        this.keysLeft.indexOf(evt.key) !== -1 ||
                        this.keysRight.indexOf(evt.key) !== -1
                    ) {
                        const index = this._keys.indexOf(evt.key)

                        if (index === -1) {
                            this._keys.push(evt.key)
                        }
                        if (!noPreventDefault) {
                            evt.preventDefault()
                        }
                    }
                } else {
                    if (
                        this.keysUp.indexOf(evt.key) !== -1 ||
                        this.keysDown.indexOf(evt.key) !== -1 ||
                        this.keysLeft.indexOf(evt.key) !== -1 ||
                        this.keysRight.indexOf(evt.key) !== -1
                    ) {
                        const index = this._keys.indexOf(evt.key)

                        if (index >= 0) {
                            this._keys.splice(index, 1)
                        }
                        if (!noPreventDefault) {
                            evt.preventDefault()
                        }
                    }
                }
            },
        )
    }

    /**
     * Detach the current controls from the specified dom element.
     */
    detachControl(): void {
        if (this._scene) {
            if (this._onKeyboardObserver) {
                this._scene.onKeyboardObservable.remove(
                    this._onKeyboardObserver,
                )
            }

            if (this._onCanvasBlurObserver) {
                this._engine.onCanvasBlurObservable.remove(
                    this._onCanvasBlurObserver,
                )
            }
            this._onKeyboardObserver = null
            this._onCanvasBlurObserver = null
        }
        this._keys = []
    }

    /**
     * Update the current camera state depending on the inputs that have been used this frame.
     * This is a dynamically created lambda to avoid the performance penalty of looping for inputs in the render loop.
     */
    checkInputs(): void {
        if (this._onKeyboardObserver) {
            const camera = this.camera
            // Keyboard
            for (let index = 0; index < this._keys.length; index++) {
                const key = this._keys[index]

                if (this.keysLeft.indexOf(key) !== -1) {
                    SimpleCameraInput.turn(camera, true)
                } else if (this.keysRight.indexOf(key) !== -1) {
                    SimpleCameraInput.turn(camera, false)
                } else {
                    if (this.keysUp.indexOf(key) !== -1) {
                        SimpleCameraInput.move(camera, true)
                    } else if (this.keysDown.indexOf(key) !== -1) {
                        SimpleCameraInput.move(camera, false)
                    }
                }
            }
        }
    }

    static readonly MOVE_SPEED_MULTIPLIER = 0.6

    static readonly TURN_SPEED = 0.005
    static turn = (camera: FreeCamera, left: boolean, multiplier = 1) =>
        (camera.cameraRotation.y +=
            SimpleCameraInput.TURN_SPEED * (left ? -multiplier : multiplier))

    static move = (
        camera: FreeCamera,
        forward: boolean,
        multiplier = SimpleCameraInput.MOVE_SPEED_MULTIPLIER,
    ) => {
        const speed = camera._computeLocalCameraSpeed() * multiplier
        camera._localDirection.copyFromFloats(
            0,
            0,
            forward ? speed : -speed * 0.5,
        )

        if (camera.getScene().useRightHandedSystem) {
            camera._localDirection.z *= -1
        }

        camera.getViewMatrix().invertToRef(camera._cameraTransformMatrix)
        Vector3.TransformNormalToRef(
            camera._localDirection,
            camera._cameraTransformMatrix,
            camera._transformedDirection,
        )
        camera.cameraDirection.addInPlace(camera._transformedDirection)
    }

    // noinspection JSUnusedGlobalSymbols
    /**
     * Gets the class name of the current intput.
     * @returns the class name
     */
    getClassName = () => "FreeCameraKeyboardMoveInput"

    // noinspection JSUnusedGlobalSymbols
    /** @hidden */
    _onLostFocus(e: FocusEvent): void {
        this._keys = []
    }

    // noinspection JSUnusedGlobalSymbols
    /**
     * Get the friendly name associated with the input class.
     * @returns the input friendly name
     */
    getSimpleName = () => "keyboard-simple"
}
